<script lang="ts" setup>
import { useHead } from '@unhead/vue'
import { useTheme } from 'vuetify'

import UserInboxDrawer from '@/components/inbox/UserInboxDrawer.vue'
import SessionExpiryModal from '@/components/SessionExpiryModal.vue'
import { useUserInboxNotificationsInit } from '@/composables/useUserInboxNotificationsInit'
import DefaultLayout from '@/layouts/default.vue'
import ProviderLayout from '@/layouts/provider.vue'
import { isPublicRoute } from '@/router/utils'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { useThemeConfig } from '@core/composable/useThemeConfig'
import { hexToRgb } from '@layouts/utils'

const { isAppRtl } = useThemeConfig()

const { global } = useTheme()

const route = useRoute()

useUserInboxNotificationsInit()

const auth = useAuthStore()
const user = useUserStore()
const router = useRouter()

function syncAuthentication(event: StorageEvent) {
  if (event.key !== 'isAuthenticated' && event.key !== null)
    return

  auth.syncAuthenticated()

  if (!auth.isLoggedIn) {
    user.$reset()

    // A logout in another tab must not throw this one off a public page — the
    // visitor may never have been the one signed in. (VH-2377)
    if (!isPublicRoute(route))
      router.push('/login')
  }
}

onMounted(() => window.addEventListener('storage', syncAuthentication))
onUnmounted(() => window.removeEventListener('storage', syncAuthentication))

const showVectorInbox = computed(
  () => auth.isLoggedIn && Boolean(user.data?.access?.vector),
)

// Create refs for the shared variables
const isVector = ref(localStorage.getItem('isVector') === 'true')

// Function to set the values in localStorage
function setLocalStorage() {
  localStorage.setItem('isVector', isVector.value.toString())
}

// Watch for changes in isVector and isSponsor and update localStorage
watch(isVector, setLocalStorage)

// Provide the variables to the entire application
provide('isVector', isVector)

onErrorCaptured(e => {
  if (import.meta.env.VITE_NODE_ENV === 'local')
    console.error(e)

  return false
})

// Meta Info
const siteData = reactive({
  title: 'VectorHealth',
  description:
    'The platform connecting healthcare teams, nonprofits, and patients in need of financial aid.',
})

function useTitleTemplate(baseTitle: Ref<string | undefined>) {
  return computed(() => {
    const t = baseTitle.value?.trim() ?? ''
    if (t === '')
      return 'VectorHealth'

    return `${t} | VectorHealth`
  })
}

// Stop error resizeObserver
function debounce(callback: (...args: any[]) => void, delay: number) {
  let tid: any

  return function (...args: any[]) {
    const ctx = self

    tid && clearTimeout(tid)
    tid = setTimeout(() => {
      callback.apply(ctx, args)
    }, delay)
  }
}

const _ = (window as any).ResizeObserver

;(window as any).ResizeObserver = class ResizeObserver extends _ {
  constructor(callback: (...args: any[]) => void) {
    callback = debounce(callback, 20)
    super(callback)
  }
}

const siteTitle = toRef(siteData, 'title')
const currentFullTitle = ref(useTitleTemplate(siteTitle).value)

// Public pages are reachable with no session, so the idle-expiry countdown (and its
// authenticated heartbeat) must not run there. (VH-2377)
const isOnPublicRoute = computed(() => isPublicRoute(route))

const isBlank = computed(() => (route.meta?.layout ?? 'default') === 'blank')
const isProvider = computed(() => route.meta?.layout === 'provider')

watch(
  () => route.meta.title,
  newTitle => {
    const titleRef = ref(typeof newTitle === 'string' ? newTitle : '')

    currentFullTitle.value = useTitleTemplate(titleRef).value
  },
)

useHead({
  title: () => currentFullTitle.value,
  meta: [
    {
      name: 'description',
      content: computed(() => siteData.description),
    },
  ],
})
</script>

<template>
  <VLocaleProvider :rtl="isAppRtl">
    <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary -->
    <VApp :style="`--v-global-theme-primary: ${hexToRgb(global.current.value.colors.primary)}`">
      <!-- Provider layout: clean top bar with insurance nav, no vector nav -->
      <ProviderLayout v-if="isProvider" />

      <!-- If not blank/provider, mount the default layout (it includes its own RouterView) -->
      <DefaultLayout v-else-if="!isBlank" />

      <!-- If blank, render the page directly -->
      <RouterView v-else />

      <UserInboxDrawer v-if="showVectorInbox" />
      <TermsModal v-if="route.params.charity_id" />
      <SessionExpiryModal v-if="auth.isLoggedIn && !isOnPublicRoute" />
      <ScrollToTop />
    </VApp>
  </VLocaleProvider>
</template>
